<template>
  <div class="flex flex-col gap-6">
    <div class="bg-white shadow-md rounded-md p-4 flex flex-col gap-4">
      <div class="text-left relative">
        <label class="text-sm text-neutral-700 font-medium mb-1 inline-block">{{ $t('order.createOrder.stepOne.senderAddress') }}</label>
        <div class="relative">
          <TextField
            withIcon
            type="text"
            @input="searchSenderAddress($event, 'senderAddress')"
            v-model="tempOrderDetail.sender_address"
            :placeholder="$t('order.createOrder.stepOne.senderAddressPlaceholder')"
          />
          <div class="absolute left-3 top-1/2 transform -translate-y-1/2">
            <Search />
          </div>
          <img src="@/assets/images/rolling.gif" class="w-8 absolute top-1/2 transform -translate-y-1/2 right-2" v-if="isSearching['senderAddress']" />
          <transition name="fade" mode="out-in">
            <div class="mt-1 z-10 border rounded-lg absolute top-full left-0 w-full bg-white" v-if="visibleSuggestion['senderAddress']">
              <ul v-if="addresses" class="max-h-40 overflow-y-scroll shadow-base">
                <li class="cursor-pointer px-3 py-2 hover:bg-neutral-300 " v-for="(address, index) in addresses" :key="'address-' + index" @click="selectSenderAddress(address, 'senderAddress')">
                  {{ address.location_label }}
                </li>
              </ul>
              <div class="py-4 text-center" v-else>
                {{ $t('order.createOrder.stepOne.dataNotFound') }}
              </div>
            </div>
          </transition>
        </div>
      </div>
      <TextField type="text" :label="$t('order.createOrder.stepOne.receiverName')" v-model="tempOrderDetail.receiver_name" :placeholder="$t('order.createOrder.stepOne.receiverNamePlaceholder')" />
      <TextField
        type="text"
        @keypress="numbersOnly"
        :label="$t('order.createOrder.stepOne.receiverPhone')"
        v-model="tempOrderDetail.receiver_phone"
        :placeholder="$t('order.createOrder.stepOne.receiverPhonePlaceholder')"
      />
      <div class="text-left relative">
        <label class="text-sm text-neutral-700 font-medium mb-1 inline-block">{{ $t('order.createOrder.stepOne.receiverDistrict') }}</label>
        <div class="relative">
          <TextField withIcon type="text" @input="searchCity($event, 'destination')" v-model="destinationSearch" :placeholder="$t('order.createOrder.stepOne.receiverDistrictPlaceholder')" />
          <div class="absolute left-3 top-1/2 transform -translate-y-1/2">
            <Search />
          </div>
          <img src="@/assets/images/rolling.gif" class="w-8 absolute top-1/2 transform -translate-y-1/2 right-2" v-if="isSearching['destination']" />
          <transition name="fade" mode="out-in">
            <div class="mt-1 z-10 border rounded-lg absolute top-full left-0 w-full bg-white" v-if="visibleSuggestion['destination']">
              <ul v-if="cities" class="max-h-40 overflow-y-scroll">
                <li class="cursor-pointer px-3 py-2 hover:bg-neutral-300 " v-for="(city, index) in cities" :key="'city-' + index" @click="selectCity(city, 'destination')">
                  {{ city.village }}, {{ city.area_display }}
                </li>
              </ul>
              <div class="py-4 text-center" v-else>
                {{ $t('order.createOrder.stepOne.dataNotFound') }}
              </div>
            </div>
          </transition>
        </div>
      </div>
      <Textarea :label="$t('order.createOrder.stepOne.receiverAddress')" v-model="tempOrderDetail.receiver_address" :placeholder="$t('order.createOrder.stepOne.receiverAddressPlaceholder')" />

      <div class="custom-select">
        <label class="text-sm text-neutral-700 font-medium mb-1 inline-block">{{ $t('order.createOrder.stepOne.packageType') }}</label>
        <v-select
          :options="goodsCategories"
          :placeholder="$t('order.createOrder.stepOne.packageTypePlaceholder')"
          :reduce="selectedPackage => selectedPackage.code"
          label="name"
          v-model="tempOrderDetail.goods_category"
        />
      </div>
      <TextField v-model="tempOrderDetail.goods_weight" :label="'Berat (kg)'" />
      <div class="grid grid-cols-3 gap-4">
        <TextField v-model="tempOrderDetail.goods_length" :label="'Panjang (cm)'" />
        <TextField v-model="tempOrderDetail.goods_width" :label="'Lebar (cm)'" />
        <TextField v-model="tempOrderDetail.goods_height" :label="'Tinggi (cm)'" />
      </div>
      <div>
        <label class="text-sm text-neutral-700 font-medium mb-1 inline-block">
          Harga Barang (Rp)
        </label>
        <CurrencyInput
          :options="{ currency: 'IDR' }"
          :value="tempOrderDetail.goods_value"
          @change="tempOrderDetail.goods_value = $event"
          :placeholder="$t('order.createOrder.stepTwo.goodsValuePlaceholder')"
        />
      </div>
      <div class="text-left relative">
        <label class="text-sm text-neutral-700 font-medium mb-1 inline-block">{{ $t('order.createOrder.stepOne.deliveryService') }}</label>
        <div class="relative">
          <select id="deliveryService" v-model="tempOrderDetail.delivery_service_code" class="border rounded-lg p-2 w-full">
            <option disabled value="">-- Please choose a service --</option>
            <option v-for="service in deliveryServiceCodes" :key="service" :value="service">
              {{ service }}
            </option>
          </select>
        </div>
      </div>
      <Button :buttonText="isLoadingEdit ? 'Saving...' : 'Edit'" :disabled="isLoadingEdit" :isLoading="isLoadingEdit" @action="submitForm" />
    </div>
  </div>
</template>

<script>
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css'
import { mapActions, mapGetters } from 'vuex'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'

export default {
  components: {
    TextField: () => import(/* webpackChunkName: "Forms" */ '@/components/Forms/TextField'),
    CurrencyInput: () => import(/* webpackChunkName: "Forms" */ '@/components/Forms/CurrencyInput'),
    Search: () => import(/* webpackChunkName: "Icons" */ '@/components/Icons/Search'),
    Button: () => import(/* webpackChunkName: "Forms" */ '@/components/Forms/Button'),
    Textarea: () => import(/* webpackChunkName: "Forms" */ '@/components/Forms/Textarea'),
    vSelect
  },
  data: () => ({
    isLoadingEdit: false,
    isUsingExistingReceiverAddress: false,
    visibleAddressModal: false,
    visibleCustom: false,
    goodsWeight: 0,
    addressDetail: null,
    packageList: ['a', 'b'],
    selectedPackage: null,
    selectedAddress: null,
    selectedReceiverAddress: null,
    searchKeyword: null,
    isSearchingPlace: false,
    selectedPackageSize: null,
    uploadIsLoading: false,
    destinationSearch: '',
    uploadedFile: null,
    isFetching: false,
    payloads: {
      sender_name: '',
      sender_phone: '',
      sender_address: '',
      receiver_name: '',
      receiver_phone: '',
      receiver_address: '',
      receiver_district: '',
      receiver_subdistrict: '',
      receiver_village: '',
      receiver_postal_code: '',
      goods_category: '',
      goods_weight: '',
      goods_dimension: '',
      goods_value: '',
      price: '',
      delivery_service_code: ''
    },
    cityKeyword: null,
    visibleSuggestion: {
      origin: false,
      destination: false,
      senderAddress: false,
      receiverAddress: false
    },
    isSearching: {
      origin: false,
      destination: false,
      senderAddress: false,
      receiverAddress: false
    },
    senderAddressSearch: '',
    senderAddressKeyword: '',
    receiverAddressSearch: '',
    receiverAddressKeyword: '',
    address: null,
    autocomplete: null,
    autocompleteService: null,
    placeService: null,
    sessionToken: null,
    suggestionList: [],
    localStorageAsync: {
      set: function(key, value) {
        return Promise.resolve().then(function() {
          localStorage.setItem(key, value)
        })
      },
      get: function(key) {
        return Promise.resolve().then(function() {
          return localStorage.getItem(key)
        })
      }
    },
    isPOD: false,
    queries: {
      page: 1,
      per_page: 10,
      search_keyword: '',
      start_date: '',
      end_date: '',
      status: 'ALL',
      sort: 'DESC'
    }
  }),
  computed: {
    ...mapGetters('master', ['cities', 'packageSizes', 'goodsCategories', 'pickupSlotTime', 'deliveryServiceCodes']),
    ...mapGetters('merchant', ['addresses', 'isAddressModalVisible']),
    ...mapGetters('common', ['isMobile']),
    ...mapGetters('order', ['tempOrderDetail']),
    tempOrderId() {
      return this.$route.params.id
    },

    isStepAvailable() {
      return this.orderStep.indexOf(this.$route.params.step) > -1
    },
    start() {
      return (this.queries.page - 1) * this.queries.per_page + 1
    },
    end() {
      const lastOnPage = (this.queries.page - 1) * this.queries.per_page + this.queries.per_page
      return lastOnPage > this.totalRecords ? this.totalRecords : lastOnPage
    },
    tabList() {
      return [
        {
          label: this.$t('order.tab.all'),
          value: 'ALL'
        },
        {
          label: this.$t('order.tab.ongoing'),
          value: 'ONGOING'
        },
        {
          label: this.$t('order.tab.unpaid'),
          value: 'UNPAID'
        },
        {
          label: this.$t('order.tab.completed'),
          value: 'COMPLETED'
        },
        {
          label: this.$t('order.tab.complain'),
          value: 'COMPLAIN'
        }
      ]
    },
    defaultAddress() {
      return this.addresses.filter(item => item.is_main)[0]
    },
    isFirstStep() {
      return this.$route.params.step === 'shipping'
    },
    isSecondStep() {
      return this.$route.params.step === 'logistic'
    },
    isThirdStep() {
      return this.$route.params.step === 'confirmation'
    },
    isCustomDisabled() {
      return !this.validForm(this.custom)
    },
    isDisabled() {
      return (
        !this.selectedAddress ||
        !this.receiverName ||
        !this.receiverPhoneNumber ||
        !this.destinationSearch ||
        !this.addressDetail ||
        !this.selectedPackage ||
        !this.selectedPackageSize ||
        (this.isScheduleOrder && !this.scheduleDate) ||
        (this.isScheduleOrder && !this.scheduleTime)
      )
    },
    goodsDimension() {
      const l = this.tempOrderDetail.goods_length
      const w = this.tempOrderDetail.goods_width
      const h = this.tempOrderDetail.goods_height

      // return null if all are empty
      if (!l && !w && !h) return null

      return `${l || 0}x${w || 0}x${h || 0}`
    }
  },
  watch: {
    $route(val) {
      if (Object.keys(val.query).length > 0) {
        this.getTempOrderList(this.queries)
      } else {
        this.$router.push({ path: '/order/step/upload', query: this.queries })
      }
    }
  },
  created() {
    this.getGoodsCategoryList()
    this.getTempOrderInfo()
    this.getDeliveryServiceCodeList()
  },
  methods: {
    ...mapActions('master', ['getCities', 'getGoodsCategories', 'getDeliveryServiceCodes']),
    ...mapActions('order', ['getTempOrderDetail', 'updateTempOrder']),
    ...mapActions('common', ['showLoading', 'hideLoading']),
    ...mapActions('merchant', ['getAddresses', 'getDeliveryServices', 'toggleAddressModal']),
    getGoodsCategoryList() {
      this.getGoodsCategories()
    },
    getDeliveryServiceCodeList() {
      this.getDeliveryServiceCodes()
    },
    getTempOrderInfo() {
      this.showLoading()
      this.getTempOrderDetail({
        tempOrderId: this.tempOrderId
      }).then(() => {
        this.hideLoading()

        // 🔹 auto-split goods_dimension into L/W/H
        if (this.tempOrderDetail.goods_dimension) {
          const [l, w, h] = this.tempOrderDetail.goods_dimension.split('x').map(v => v.trim())
          this.tempOrderDetail.goods_length = l || ''
          this.tempOrderDetail.goods_width = w || ''
          this.tempOrderDetail.goods_height = h || ''
        }

        this.destinationSearch = `${this.tempOrderDetail.receiver_village}, ${this.tempOrderDetail.receiver_district}`
      })
    },
    submitForm() {
      this.isLoadingEdit = true
      this.payloads = {
        sender_name: this.tempOrderDetail.sender_name,
        sender_phone: this.tempOrderDetail.sender_phone,
        sender_address: this.tempOrderDetail.sender_address,
        sender_location_code: this.tempOrderDetail.sender_location_code,
        receiver_name: this.tempOrderDetail.receiver_name,
        receiver_phone: this.tempOrderDetail.receiver_phone,
        receiver_address: this.tempOrderDetail.receiver_address,
        receiver_district: this.tempOrderDetail.receiver_district,
        receiver_subdistrict: this.tempOrderDetail.receiver_subdistrict,
        receiver_village: this.tempOrderDetail.receiver_village,
        receiver_postal_code: this.tempOrderDetail.receiver_postal_code,
        receiver_location_code: this.tempOrderDetail.receiver_location_code,
        goods_category: this.tempOrderDetail.goods_category,
        goods_weight: this.tempOrderDetail.goods_weight,
        // 🔹 instead of separate fields, combine into one
        goods_dimension: `${this.tempOrderDetail.goods_length || 0}x${this.tempOrderDetail.goods_width || 0}x${this.tempOrderDetail.goods_height || 0}`,
        goods_value: this.tempOrderDetail.goods_value,
        delivery_service_code: this.tempOrderDetail.delivery_service_code
      }

      this.updateTempOrder({
        tempOrderId: this.tempOrderId,
        payloads: this.payloads
      })
        .then(() => {
          this.$router.push(`/order/step/upload`)
        })
        .finally(() => {
          this.isLoadingEdit = false
        })
    },
    searchSenderAddress(value, target) {
      this.senderAddressKeyword = null
      if (value) this.isSearching[target] = true
      this.visibleSuggestion[target] = false
      clearTimeout(this.debounce)
      this.debounce = setTimeout(() => {
        if (value) {
          this.senderAddressKeyword = value
          this.getAddresses({
            params: {
              location_label: this.senderAddressKeyword
            }
          }).then(() => {
            this.visibleSuggestion[target] = true
            this.isSearching[target] = false
          })
        }
      }, 200)
    },
    selectSenderAddress(data, target) {
      this.tempOrderDetail.sender_name = data.sender_name
      this.tempOrderDetail.sender_phone = data.sender_phone
      this.tempOrderDetail.sender_location_code = data.area.code
      this.tempOrderDetail.sender_address = data.detail_address
      this.selectedAddress = data
      this.visibleSuggestion[target] = false
    },

    searchReceiverAddress(value, target) {
      this.receiverAddressKeyword = null
      if (value) this.isSearching[target] = true
      this.visibleSuggestion[target] = false
      clearTimeout(this.debounce)
      this.debounce = setTimeout(() => {
        if (value) {
          this.receiverAddressKeyword = value
          this.getAddresses({
            params: {
              location_label: this.receiverAddressKeyword
            }
          }).then(() => {
            this.visibleSuggestion[target] = true
            this.isSearching[target] = false
          })
        }
      }, 200)
    },
    selectReceiverAddress(data, target) {
      this.receiverAddressSearch = data.location_label
      this.selectedReceiverAddress = data
      this.visibleSuggestion[target] = false
      this.tempOrderDetail.receiver_name = data.receiver_name
      this.tempOrderDetail.receiver_phone = data.receiver_phone
      this.tempOrderDetail.receiver_address = data.detail_address
      this.tempOrderDetail.receiver_district = data.area.district
      this.tempOrderDetail.receiver_subdistrict = data.area.subdistrict
      this.tempOrderDetail.receiver_village = data.area.village
      this.tempOrderDetail.receiver_postal_code = data.area.postal_code
      this.tempOrderDetail.receiver_location_code = data.area.code
    },
    searchCity(value, target) {
      this.cityKeyword = null
      if (value) this.isSearching[target] = true
      this.visibleSuggestion[target] = false
      clearTimeout(this.debounce)
      this.debounce = setTimeout(() => {
        if (value) {
          this.cityKeyword = value
          this.getCities({
            params: {
              page: 1,
              per_page: 1000,
              search_keyword: this.cityKeyword
            }
          }).then(() => {
            this.visibleSuggestion[target] = true
            this.isSearching[target] = false
          })
        }
      }, 200)
    },
    selectCity(data, target) {
      this.receiverAddressSearch = data.location_label
      this.selectedReceiverAddress = data
      this.visibleSuggestion[target] = false
      this.tempOrderDetail.receiver_name = data.receiver_name
      this.tempOrderDetail.receiver_phone = data.receiver_phone
      this.tempOrderDetail.receiver_district = data.district
      this.tempOrderDetail.receiver_subdistrict = data.subdistrict
      this.tempOrderDetail.receiver_village = data.village
      this.tempOrderDetail.receiver_postal_code = data.postal_code
      this.tempOrderDetail.receiver_location_code = data.code

      this.destinationSearch = `${this.tempOrderDetail.receiver_village}, ${this.tempOrderDetail.receiver_district}`
    },
    numbersOnly(evt) {
      evt = evt ? evt : window.event
      var charCode = evt.which ? evt.which : evt.keyCode
      if (charCode > 31 && (charCode < 48 || charCode > 57) && charCode !== 46) {
        evt.preventDefault()
      } else {
        return true
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.custom-select {
  &::v-deep .vs__dropdown-toggle {
    padding: 12px;
    border-radius: 8px;
  }

  &::v-deep .vs__actions {
    padding: 0;
  }

  &::v-deep .vs__search,
  &::v-deep .vs__selected {
    padding: 0;
    margin: 0;
  }

  &::v-deep .vs__search::placeholder {
    /* Chrome, Firefox, Opera, Safari 10.1+ */
    color: #748497;
    opacity: 1;
    /* Firefox */
  }
}
</style>
